import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  forwardRef,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import { CSSInterpolation } from '@mui/system';

import { styled, Theme } from '@mui/material/styles';

import Button, { buttonClasses, ButtonProps } from '../Button';
import { ColorList } from '../theme/colorList';
import colors from '../theme/colors';
import Box from '../Box';
import { unstable_composeClasses as composeClasses } from '@mui/utils';
import productCardClasses, { getToolbarUtilityClass } from './productCardClasses';
import { clsx } from 'clsx';
import capitalize from 'lodash/capitalize';
import { BoxProps } from '@mui/material/Box';
import { ProductCardBody, ProductCardBodyProps } from './ProductCardBody';

/**
 * Additional props shared between ownerState and the public API.
 * @internal
 */
export interface ProductCardSharedProps {
  /**
   * What color to show the 1px solid border in.
   * @default 'gray'
   */
  borderColor?: ColorList | 'gray';

  /**
   * ProductCard size
   * @default 'medium'
   */
  size?: 'x-small' | 'small' | 'medium';

  /**
   * Background color.
   * @default 'transparent'
   */
  color?: 'internal' | 'white' | 'transparent';

  /**
   * Displays Chip over the top left corner and extends styles for absolute positioned Chip element.
   */
  chip?: ReactNode;
  /**
   * Defaults to ChevronRight.
   */
  endIcon?: ReactElement;
  startIcon?: ReactElement;
  topContentColor?: 'primary' | 'secondary' | 'internal' | 'success' | 'warning' | 'error' | 'white';
  bottomContentColor?: 'primary' | 'secondary' | 'internal' | 'success' | 'warning' | 'error' | 'white';
  linkless?: boolean;
}

interface ProductCardRootProps extends ButtonProps {
  ownerState: ProductCardSharedProps;
}

interface BoxRootProps extends BoxProps {
  ownerState: ProductCardSharedProps;
}

export const useUtilityClasses = (
  ownerState: ProductCardSharedProps & {
    classes?: Record<string, string>;
    hideIcon?: boolean;
    topContentHover?: boolean;
    bottomContentHover?: boolean;
  }
) => {
  const {
    classes,
    size,
    chip,
    color,
    borderColor,
    startIcon,
    endIcon,
    hideIcon,
    topContentColor,
    bottomContentColor,
    topContentHover,
    bottomContentHover,
  } = ownerState;

  const slots = {
    root: [
      'root',
      size && `size${capitalize(size)}`,
      color && `color${capitalize(color)}`,
      borderColor && `borderColor${capitalize(borderColor)}`,
      Boolean(chip) && `hasChip`,
      Boolean(startIcon) && 'hasStartIcon',
      Boolean(endIcon) && 'hasEndIcon',
      Boolean(hideIcon) && 'hiddenEndIcon',
      Boolean(topContentHover) && 'topContentHover',
      Boolean(bottomContentHover) && 'bottomContentHover',
    ],
    chipWrapper: ['chipWrapper'],
    topContent: ['topContent', topContentColor && `color${capitalize(topContentColor)}`],
    mainContent: ['mainContent'],
    bottomContent: ['bottomContent', bottomContentColor && `color${capitalize(bottomContentColor)}`],
    startIcon: ['startIcon'],
    endIcon: ['endIcon', hideIcon && 'hidden'],
    defaultChevron: ['chevron-compensateRight'],
    closeButton: ['closeButton'],
  };

  const composedClasses = composeClasses(slots, getToolbarUtilityClass, classes);

  return {
    ...classes,
    ...composedClasses,
  };
};

interface StyleRootProps {
  theme: Theme;
  ownerState: ProductCardSharedProps;
}

const styleRoot = ({
  ownerState: { borderColor, color, size, chip, topContentColor, bottomContentColor, linkless },
  theme,
}: StyleRootProps & (BoxProps | ButtonProps)): CSSInterpolation => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  height: 'auto',
  width: '100%',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  borderWidth: '2px',
  borderColor: !borderColor || borderColor === 'gray' ? colors.transparentGray[200] : theme.palette[borderColor].main,
  borderStyle: 'solid',
  borderRadius: '6px',
  fontWeight: theme.typography.fontWeightRegular,
  textAlign: 'left',
  color: colors.transparentGray[900],
  userSelect: 'text',
  [theme.breakpoints.up('sm')]: {
    height: '100%',
  },
  [theme.breakpoints.only('xs')]: {
    // = (maxXsWidth-containerMargins-paperPaddings-cardContainerGap)/2 = (599-2*8-2*16-12)/2
    maxWidth: '269px',
  },
  [`&.${buttonClasses.root}`]: {
    whiteSpace: 'normal',
    padding: 0,
  },
  [`.${productCardClasses.mainContent}`]: {
    flex: 1,
    padding: theme.spacing(2.5, 2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2.5, 2.5),
    },
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(3, 3),
    },
  },
  [`.${productCardClasses.startIcon}`]: {
    alignSelf: 'center',
    display: 'grid',
    gridAutoFlow: 'column',
    color: colors.solidGray[600],
    marginRight: theme.spacing(1.5),
    '& > *': {
      fontSize: theme.typography.pxToRem(20),
      ...((size === 'x-small' || size === 'small') && {
        fontSize: theme.typography.pxToRem(16),
      }),
    },
  },

  [`.${productCardClasses.endIcon}`]: {
    alignSelf: 'center',
    display: 'grid',
    gridAutoFlow: 'column',
    gap: 16,
    marginLeft: theme.spacing(1),
    color: colors.transparentGray[400],
    ...((size === 'x-small' || size === 'small') && {
      gap: 12,
    }),
    '& .MuiSvgIcon-root': {
      marginRight: `-${theme.spacing(0.5)}`,
    },
    '& > *:nth-of-type(n)': {
      fontSize: theme.typography.pxToRem(24),
      ...((size === 'x-small' || size === 'small') && {
        fontSize: theme.typography.pxToRem(18),
      }),
      transition: theme.transitions.create(['color'], { duration: theme.transitions.duration.short }),
    },

    '&.O2ProductCard-chevron-compensateRight .MuiSvgIcon-root': {
      marginRight: `-${theme.spacing(1)}`,
    },
  },

  [`&, &.${productCardClasses.topContentHover}, &.${productCardClasses.bottomContentHover}`]: {
    backgroundColor:
      color === 'internal'
        ? theme.palette.background.internal
        : color === 'white'
        ? theme.palette.common.white
        : 'transparent',
  },
  [`.${productCardClasses.topContent}, .${productCardClasses.bottomContent}`]: {
    padding: theme.spacing(1.75, 2.5),
  },
  [`.${productCardClasses.topContent}`]: {
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
    backgroundColor:
      topContentColor && topContentColor !== 'secondary'
        ? theme.palette.background[topContentColor]
        : topContentColor === 'secondary'
        ? colors.brandSecondary[100]
        : 'transparent',
  },
  [`.${productCardClasses.bottomContent}`]: {
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    backgroundColor:
      bottomContentColor && bottomContentColor !== 'secondary'
        ? theme.palette.background[bottomContentColor]
        : bottomContentColor === 'secondary'
        ? colors.brandSecondary[100]
        : 'transparent',
  },

  [`&.${productCardClasses.topContentHover}, &.${productCardClasses.bottomContentHover}`]: {
    cursor: 'initial',
  },

  ...(!linkless && {
    [`&:not(.${productCardClasses.topContentHover},.${productCardClasses.bottomContentHover}):hover`]: {
      backgroundColor: theme.palette.background.primary,
      [`& .${buttonClasses.endIcon} > *`]: {
        color: theme.palette.primary.main,
      },
      [`.${productCardClasses.closeButton}`]: {
        visibility: 'visible',
        opacity: 1,
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.standard,
          delay: theme.transitions.duration.shorter,
        }),
      },
    },
  }),

  ...(chip && {
    position: 'relative',
    marginTop: 8,
    [`.${productCardClasses.chipWrapper} > .MuiChip-root:first-of-type`]: {
      ...(color === 'internal' && {
        backgroundColor: theme.palette.background.internal,
      }),
    },
    [`.${productCardClasses.chipWrapper}:hover > .MuiChip-root:first-of-type`]: {
      backgroundColor: theme.palette.background.primary,
    },
  }),

  [`.${productCardClasses.closeButton}`]: {
    position: 'absolute',
    top: -11,
    right: -11,
    padding: '9px',
    visibility: 'hidden',
    opacity: 0,
    zIndex: 1,
    transition: `${theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.standard,
    })}, ${theme.transitions.create(['visibility'], {
      duration: 0,
      delay: theme.transitions.duration.standard,
    })}`,
    borderColor: !borderColor || borderColor === 'gray' ? colors.transparentGray[200] : theme.palette[borderColor].main,
    backgroundColor: 'white',
    ...(!linkless && {
      '&:hover': {
        backgroundColor: theme.palette.background.primary,
      },
    }),
    '.MuiSvgIcon-root': {
      fontSize: '16px',
    },
    '@media (hover: none)': {
      visibility: 'visible',
      opacity: 1,
    },
  },
  '.MuiTouchRipple-root': {
    borderRadius: '4px',
  },
});

const ProductCardRoot = styled(Button, {
  name: 'O2ProductCard',
  slot: 'Root',
})<ProductCardRootProps>(styleRoot);

const BoxRoot = styled(Box, {
  name: 'O2ProductCard',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<BoxRootProps>(styleRoot);

export type ProductCardProps<T extends ElementType = 'button'> = (
  | Omit<ButtonProps<T>, 'size' | 'color' | 'startIcon' | 'endIcon'>
  | BoxProps<T>
) &
  Omit<ProductCardBodyProps, 'setTopContentHover' | 'setBottomContentHover'> & {
    /**
     * What component to render this component as (inheriting props).
     * Useful when combining with e.g. react-router.
     */
    component?: T;

    ref?: ComponentPropsWithRef<T>['ref'];
  } & ComponentPropsWithoutRef<T>;

const ProductCard = forwardRef(
  <T extends ElementType = 'button'>(
    {
      borderColor = 'gray',
      children,
      color,
      endIcon,
      startIcon,
      hideIcon = false,
      linkless = false,
      size = 'medium',
      chip,
      topContent,
      bottomContent,
      topContentColor,
      bottomContentColor,
      enableTopContentClick = false,
      enableBottomContentClick = false,
      onClose,
      onClick,
      className,
      ...other
    }: ProductCardProps<T>,
    ref: ProductCardProps<T>['ref']
  ) => {
    const [topContentHover, setTopContentHover] = useState(false);
    const [bottomContentHover, setBottomContentHover] = useState(false);

    const classes = useUtilityClasses({
      classes: other.className,
      size,
      chip,
      color,
      borderColor,
      startIcon,
      endIcon,
      hideIcon,
      topContentColor,
      topContentHover: !enableTopContentClick && topContentHover,
      bottomContentColor,
      bottomContentHover: !enableBottomContentClick && bottomContentHover,
    });

    const productBody = (
      <ProductCardBody
        endIcon={endIcon}
        startIcon={startIcon}
        hideIcon={hideIcon}
        linkless={linkless}
        size={size}
        chip={chip}
        topContent={topContent}
        bottomContent={bottomContent}
        topContentColor={topContentColor}
        bottomContentColor={bottomContentColor}
        enableTopContentClick={enableTopContentClick}
        enableBottomContentClick={enableBottomContentClick}
        onClose={onClose}
        setTopContentHover={setTopContentHover}
        setBottomContentHover={setBottomContentHover}
      >
        {children}
      </ProductCardBody>
    );

    return !linkless ? (
      <ProductCardRoot
        size={size === 'x-small' ? 'small' : size}
        variant="outlined"
        color="inherit"
        disableRipple={(!enableTopContentClick && topContentHover) || (!enableBottomContentClick && bottomContentHover)}
        ref={ref}
        className={clsx(classes.root, className)}
        ownerState={{ color, borderColor, size, chip, topContentColor, bottomContentColor, linkless }}
        onClick={onClick}
        {...other}
      >
        {productBody}
      </ProductCardRoot>
    ) : (
      <BoxRoot
        size={size === 'x-small' ? 'small' : size}
        ref={ref}
        className={clsx(classes.root, className)}
        ownerState={{ color, borderColor, size, chip, topContentColor, bottomContentColor, linkless }}
        {...other}
      >
        {productBody}
      </BoxRoot>
    );
  }
);

ProductCard.displayName = 'ProductCard';

export default ProductCard;
